import { useState, useEffect} from 'react';
import { getSentimentColor, getEmotionColor } from '../lib/data';
import { Avatar, AvatarFallback } from './ui/avatar';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import { DateFilter, FilterType } from './DateFilter';
import { Users } from 'lucide-react';
import { 
  ClientPerformanceData,
  TopicPercentage,
  SentimentPercentage,
  EmotionPercentage
} from '../lib/types';

interface ClientSummaryProps {
  clientData: ClientPerformanceData[];
  onDateRangeChange: (range: { start: Date; end: Date } | null) => void;
}

const sentimentMap: Record<string, string> = {
  SP: 'strong positive',
  MP: 'moderate positive',
  WP: 'weak positive',
  N: 'neutral',
  WN: 'weak negative',
  MN: 'moderate negative',
  SN: 'strong negative',
};

export function ClientSummary({ clientData, onDateRangeChange = () => {} }: ClientSummaryProps) {
  // --- START: DATE FILTER LOGIC ---
  const [currentFilter, setCurrentFilter] = useState<{
    type: FilterType;
    date: Date | null;
  }>({ type: 'monthly', date: new Date() }); // Default to showing the current month's data

  const [expandedSummaries, setExpandedSummaries] = useState<{ [key: string]: boolean }>({}); {/*added*/}

  // This effect listens for changes from the DateFilter and calls the parent component.
    useEffect(() => {
      const { type, date } = currentFilter;
      if (!date) {
        onDateRangeChange(null);
        return;
      }
  
      const selectedDate = new Date(date);
      let start: Date;
      let end: Date;
  
      switch (type) {
        case 'weekly':
          start = new Date(selectedDate);
          start.setDate(selectedDate.getDate() - selectedDate.getDay());
          end = new Date(start);
          end.setDate(start.getDate() + 6);
          break;
        case 'monthly':
          start = new Date(selectedDate.getFullYear(), selectedDate.getMonth(), 1);
          end = new Date(selectedDate.getFullYear(), selectedDate.getMonth() + 1, 0);
          break;
        case 'daily':
        default:
          start = selectedDate;
          end = selectedDate;
          break;
      }
      
      // Ensure the 'end' date covers the entire day.
      end.setHours(23, 59, 59, 999);
  
      onDateRangeChange({ start, end });
    }, [currentFilter, onDateRangeChange]);
  
    const handleFilterChange = (filterType: FilterType, date: Date | null) => {
      setCurrentFilter({ type: filterType, date: date });
    };
    
    // Formats the date for the component's subtitle
    const formatTitleDate = () => {
      const { type, date } = currentFilter;
      if (!date) return 'all time';
      
      switch (type) {
        case 'daily':
          return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });
        case 'weekly':
          const weekStart = new Date(date);
          weekStart.setDate(date.getDate() - date.getDay());
          const weekEnd = new Date(weekStart);
          weekEnd.setDate(weekStart.getDate() + 6);
          return `the week of ${weekStart.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`;
        case 'monthly':
          return date.toLocaleDateString('en-US', { year: 'numeric', month: 'long' });
        default:
          return 'the selected period';
      }
    };

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
      {/* HEADER to include the DateFilter */}
      <div className="px-6 py-4 border-b flex items-start justify-between flex-wrap gap-4">
        <div>
          <h2 className="text-lg font-semibold">Client Summary</h2>
          <p className="text-sm text-gray-600 mt-1">
            Aggregated insights for each client.
          </p>
        </div>
        <DateFilter onFilterChange={handleFilterChange} />
      </div>

      <ScrollArea className='h-[400px]'>
        <Table>
          <TableHeader className="bg-gray-50/50">
            <TableRow>
              <TableHead className="font-medium text-gray-700 w-6">Client</TableHead>
              <TableHead className="font-medium text-gray-700 w-80">Summary</TableHead> {/*added*/}
              <TableHead className="font-medium text-gray-700 w-16 text-center">Convos</TableHead>
              <TableHead className="font-medium text-gray-700">Insights</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {/* MODIFIED to use the new 'filteredClientData' array */}
            {clientData.length > 0 ? (
              clientData.map((client) => {
                const isExpanded = expandedSummaries[client.clientId];
                const shouldTruncate = client.summary.length > 150;

                const rawSummary = client.summary.trim();
                const parts = rawSummary.split(/\n{1,2}/);
                const firstString = parts[0] || '';
                const secondString = parts.slice(1).join('\n') || '';
                return(
                  <TableRow key={client.clientId}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        <Avatar className="w-8 h-8">
                          <AvatarFallback className="bg-blue-100 text-blue-700">
                            {client.clientName.charAt(0)}
                          </AvatarFallback>
                        </Avatar>
                        <span className="font-medium text-gray-800">{client.clientName}</span>
                      </div>
                    </TableCell>
                    <TableCell className="align-top whitespace-normal break-words w-80">
                    <div className="w-full break-words">
                      <p className="text-sm text-gray-700 whitespace-pre-line break-words">
                        {isExpanded || !shouldTruncate ? (
                          <>
                            {firstString}
                            <br /><br />
                            {secondString}
                          </>
                        ) : (
                          <>
                            {`${firstString.slice(0, 150)}...`}
                          </>
                        )}
                      </p>
                      {shouldTruncate && (
                        <button
                          className="text-xs text-blue-600 hover:underline mt-1"
                          onClick={() =>
                            setExpandedSummaries((prev) => ({
                              ...prev,
                              [client.clientId]: !prev[client.clientId],
                            }))
                          }
                        >
                          {isExpanded ? "Read less" : "Read more"}
                        </button>
                      )}
                    </div>
                  </TableCell>
                    <TableCell className="text-center font-semibold text-gray-700">
                      {client.convoCount}
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col gap-2">
                        {/* Sentiments Logic (no changes needed here) */}
                        {client.sentiments?.length > 0 && (
                          <div className="flex items-center flex-wrap gap-1">
                            <span className="font-semibold text-gray-600 text-sm mr-2">Sentiments:</span>
                            {(() => {
                              const sorted = [...client.sentiments].sort((a, b) => b.percentage - a.percentage);
                              const topThree = sorted.slice(0, 3);
                              const othersTotal = sorted.slice(3).reduce((sum, s) => sum + s.percentage, 0);
                              return (
                                <>
                                  {topThree.map((s, index) => (
                                    <Badge key={`s-${index}`} className="text-white" style={{ backgroundColor: getSentimentColor(sentimentMap[s.sentiment] || s.sentiment) }}>
                                      {sentimentMap[s.sentiment] || s.sentiment}: {s.percentage.toFixed(1)}%
                                    </Badge>
                                  ))}
                                  {othersTotal > 0 && <Badge key="s-others" variant="outline">Others ({othersTotal.toFixed(1)}%)</Badge>}
                                </>
                              );
                            })()}
                          </div>
                        )}
                        {/* Emotions Logic (no changes needed here) */}
                        {client.emotions?.length > 0 && (
                          <div className="flex items-center flex-wrap gap-1">
                            <span className="font-semibold text-gray-600 text-sm mr-2">Emotions:</span>
                            {(() => {
                              const sorted = [...client.emotions].sort((a, b) => b.percentage - a.percentage);
                              const topThree = sorted.slice(0, 3);
                              const othersTotal = sorted.slice(3).reduce((sum, e) => sum + e.percentage, 0);
                              return (
                                <>
                                  {topThree.map((e, index) => (
                                    <Badge key={`e-${index}`} className="text-white" style={{ backgroundColor: getEmotionColor(e.emotion) }}>
                                      {e.emotion}: {e.percentage.toFixed(1)}%
                                    </Badge>
                                  ))}
                                  {othersTotal > 0 && <Badge key="e-others" variant="outline">Others ({othersTotal.toFixed(1)}%)</Badge>}
                                </>
                              );
                            })()}
                          </div>
                        )}
                        {/* Topics Logic (no changes needed here) */}
                        {client.topics?.length > 0 && (
                          <div className="flex items-center flex-wrap gap-1">
                            <span className="font-semibold text-gray-600 text-sm mr-2">Topics:</span>
                            {(() => {
                              const sorted = [...client.topics].sort((a, b) => b.percentage - a.percentage);
                              const topThree = sorted.slice(0, 3);
                              const othersTotal = sorted.slice(3).reduce((sum, t) => sum + t.percentage, 0);
                              return (
                                <>
                                  {topThree.map((t, index) => (
                                    <Badge key={`t-${index}`} variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                                      {t.topic} ({t.percentage.toFixed(1)}%)
                                    </Badge>
                                  ))}
                                  {othersTotal > 0 && <Badge key="t-others" variant="outline">Others ({othersTotal.toFixed(1)}%)</Badge>}
                                </>
                              );
                            })()}
                          </div>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                );
              })
              ) : (
                // MODIFIED: A much better empty state message
                <TableRow>
                  <TableCell colSpan={4} className="h-24 text-center">  {/* edited */}
                    <div className="text-center py-8 text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                      <p>No client activity found for the selected period.</p>
                    </div>
                  </TableCell>
                </TableRow>
              )
            }
          </TableBody>
        </Table>
      </ScrollArea>
    </div>
  );
}
